import firebase from 'firebase';
import {
  CustomerFromJson,
  Customer,
  StampCardHistory,
  Collection,
} from 'smartdishpro-scripts/ts_common';
import { db } from '../firebase';
import { createDocument, updateDocument } from './common';
import { functions } from '../firebase';

const _customersRef = (merchantId: string, storeId: string) =>
  db
    .collection(Collection.Merchants)
    .doc(merchantId)
    .collection(Collection.Stores)
    .doc(storeId)
    .collection(Collection.Customers);

export const getCustomer = async (
  merchantId: string,
  storeId: string,
  lineId: string
) => {
  try {
    const snapshot = await _customersRef(merchantId, storeId)
      .where('documentStatus', '==', 'active')
      .where('lineId', '==', lineId)
      .limit(1)
      .get();

    if (snapshot.empty) {
      return;
    }

    const data = snapshot.docs[0].data({ serverTimestamps: 'estimate' });
    if (!data) return undefined;
    const customer = CustomerFromJson(data, snapshot.docs[0].id);
    // 業態変更対応: Customer型未定義のためany経由で取得
    (customer as any).deauthorizedAt = data.deauthorizedAt?.toDate() ?? null;
    return customer;
  } catch (error) {
    return undefined;
  }
};

export const getCustomerDoc = async (
  merchantId: string,
  storeId: string,
  customerId: string
) => {
  const snapshot = await _customersRef(merchantId, storeId)
    .doc(customerId)
    .get();

  return snapshot.exists
    ? CustomerFromJson(
        snapshot.data({ serverTimestamps: 'estimate' }),
        snapshot.id
      )
    : undefined;
};

export const createCustomer = async (
  merchantId: string,
  storeId: string,
  lineId: string,
  displayName: string,
  pictureUrl: string | undefined,
  stampExpiredAt: Date | null,
  stampStartedAt: Date | null
) => {
  const customer: Customer = {
    createdAt: new Date(),
    deletedAt: null,
    documentStatus: 'active',
    lastVistedAt: null,
    lastOrderItems: null,
    lineId: lineId,
    mCustomerId: null,
    name: displayName,
    numOfVisit: 0,
    customerItemPreferencesMap: null,
    pictureUrl: pictureUrl ?? null,
    registerType: 'line',
    stampExpiredAt: stampExpiredAt,
    stampStartedAt: stampStartedAt,
    updatedAt: new Date(),
  };

  await _customersRef(merchantId, storeId).add({
    ...createDocument(customer),
    stampExpiredAt:
      stampExpiredAt === null
        ? null
        : firebase.firestore.Timestamp.fromDate(stampExpiredAt),
    stampStartedAt:
      stampStartedAt === null
        ? null
        : firebase.firestore.Timestamp.fromDate(stampStartedAt),
  });

  return await getCustomer(merchantId, storeId, lineId);
};

export const updateCustomer = async (
  merchantId: string,
  storeId: string,
  customerId: string,
  name: string,
  pictureUrl: string | null,
  stampExpiredAt: Date | null,
  stampStartedAt: Date | null,
  history?: StampCardHistory
) => {
  const _json = updateDocument({
    name: name,
    pictureUrl: pictureUrl,
  });
  await _customersRef(merchantId, storeId)
    .doc(customerId)
    .update({
      ..._json,
      stampExpiredAt:
        stampExpiredAt === null
          ? null
          : firebase.firestore.Timestamp.fromDate(stampExpiredAt),
      stampStartedAt:
        stampStartedAt === null
          ? null
          : firebase.firestore.Timestamp.fromDate(stampStartedAt),
    });

  if (history) {
    await _customersRef(merchantId, storeId)
      .doc(customerId)
      .collection(Collection.StampCardHistories)
      .add({
        ...createDocument(history),
        stampExpiredAt:
          stampExpiredAt === null
            ? null
            : firebase.firestore.Timestamp.fromDate(history.stampExpiredAt),
        stampStartedAt:
          stampStartedAt === null
            ? null
            : firebase.firestore.Timestamp.fromDate(history.stampStartedAt),
      });
  }
};

export const updateDeauthorizedAt = async (
  merchantId: string,
  storeId: string,
  customerId: string
) => {
  await _customersRef(merchantId, storeId)
    .doc(customerId)
    .update(updateDocument({ deauthorizedAt: firebase.firestore.FieldValue.serverTimestamp() }));
};

/**
 * ゲストCustomer作成API呼び出し
 * Firebase匿名認証を使用してゲストユーザーを作成
 */
export const createGuestCustomer = async (
  merchantId: string,
  storeId: string
): Promise<Customer> => {
  try {
    const result = await functions.httpsCallable('createGuestCustomer')({
      merchantId,
      storeId
    });
    
    const customer = result.data.customer;
    customer.id = result.data.customerId;
    
    return customer;
  } 
  catch (error) {
    console.error('Error creating guest customer:', error);
    throw new Error('ゲストユーザーの作成に失敗しました');
  }
};
